import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Typography, Space, Breadcrumb, Segmented, Alert, Spin } from 'antd';
import { HomeOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { EventList } from '../components/events/EventList';
import { EventFilters } from '../components/events/EventFilters';
import { useEvents } from '../api/queries/useEvents';
import { useTenant } from '../hooks/useTenant';
import type { JobStatus, TimeRange } from '../api/types';
import { getErrorMessage } from '../utils/error';

const { Title } = Typography;

type TableType = 'job_status' | 'stat' | 'log';

const TABLE_OPTIONS = [
  { value: 'job_status', label: 'Job Status' },
  { value: 'stat', label: 'Stats' },
  { value: 'log', label: 'Logs' },
];

export function EventListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantId, isLoading: isTenantLoading } = useTenant();

  // Parse URL params
  const tableParam = (searchParams.get('table') as TableType) || 'job_status';
  const statusParam = searchParams.get('status') as JobStatus | undefined;
  const levelParam = searchParams.get('level') as 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL' | undefined;
  const namespaceParam = searchParams.get('namespace') || undefined;
  const timeRangeParam = (searchParams.get('time_range') as TimeRange) || '24h';
  const startTimeParam = searchParams.get('start_time') || undefined;
  const endTimeParam = searchParams.get('end_time') || undefined;
  const pageParam = parseInt(searchParams.get('page') || '1', 10);

  // Local state
  const [table, setTable] = useState<TableType>(tableParam);
  const [status, setStatus] = useState<JobStatus | undefined>(statusParam);
  const [level, setLevel] = useState<'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL' | undefined>(levelParam);
  const [namespace, setNamespace] = useState<string | undefined>(namespaceParam);
  const [timeRange, setTimeRange] = useState<TimeRange>(timeRangeParam);
  const [customRange, setCustomRange] = useState<[string, string] | undefined>(
    startTimeParam && endTimeParam ? [startTimeParam, endTimeParam] : undefined
  );
  const [page, setPage] = useState(pageParam);
  const [pageSize, setPageSize] = useState(50);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('table', table);
    if (status) params.set('status', status);
    if (level) params.set('level', level);
    if (namespace) params.set('namespace', namespace);
    params.set('time_range', timeRange);
    if (timeRange === 'custom' && customRange) {
      params.set('start_time', customRange[0]);
      params.set('end_time', customRange[1]);
    }
    params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [table, status, level, namespace, timeRange, customRange, page, setSearchParams]);

  // Fetch events
  const { data, isLoading, error } = useEvents({
    table,
    status: table === 'job_status' ? status : undefined,
    level: table === 'log' ? level : undefined,
    namespace,
    time_range: timeRange,
    start_time: timeRange === 'custom' ? customRange?.[0] : undefined,
    end_time: timeRange === 'custom' ? customRange?.[1] : undefined,
    page,
    page_size: pageSize,
  });

  const handleTableChange = (value: string | number) => {
    setTable(value as TableType);
    setStatus(undefined);
    setLevel(undefined);
    setPage(1);
  };

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  const handleTimeRangeChange = (newTimeRange: TimeRange, newCustomRange?: [string, string]) => {
    setTimeRange(newTimeRange);
    if (newTimeRange === 'custom' && newCustomRange) {
      setCustomRange(newCustomRange);
    } else if (newTimeRange !== 'custom') {
      setCustomRange(undefined);
    }
    setPage(1);
  };

  if (isTenantLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <Alert
        type="warning"
        message="No Tenant Selected"
        description="Please select a tenant from the header."
        showIcon
      />
    );
  }

  return (
    <div>
      <Breadcrumb
        items={[
          {
            href: '/',
            title: (
              <>
                <HomeOutlined />
                <span>Dashboard</span>
              </>
            ),
          },
          {
            title: (
              <>
                <UnorderedListOutlined />
                <span>Events</span>
              </>
            ),
          },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
          <Title level={3} style={{ margin: 0 }}>
            Events
          </Title>
          <Segmented
            options={TABLE_OPTIONS}
            value={table}
            onChange={handleTableChange}
          />
        </Space>

        <EventFilters
          table={table}
          status={status}
          level={level}
          namespace={namespace}
          timeRange={timeRange}
          customRange={customRange}
          onStatusChange={setStatus}
          onLevelChange={setLevel}
          onNamespaceChange={setNamespace}
          onTimeRangeChange={handleTimeRangeChange}
        />

        {error && (
          <Alert
            type="error"
            message="Error loading events"
            description={getErrorMessage(error)}
            showIcon
          />
        )}

        <EventList
          events={(data?.events || []) as Parameters<typeof EventList>[0]['events']}
          loading={isLoading}
          table={table}
          page={page}
          pageSize={pageSize}
          hasMore={data?.pagination.has_more || false}
          onPageChange={handlePageChange}
        />
      </Space>
    </div>
  );
}
